import type { ActionStatus, WebhookEventName } from './actionStatus';
import type { SocialDealCopy } from './socialDeal';

export * from './actionStatus';
export * from './action';
export * from './dealsApi';
export * from './socialDeal';

/**
 * A single button on a step. The server-driven, data-first source of truth for
 * a step's actions — the client renders exactly what's here (kind → button
 * variant, action → behavior), it does not decide what becomes a button.
 * Mirrors the backend `ctas` cta object (`Deals::StepsConfigSchema`).
 */
export interface DealCta {
  /** Emphasis. primary = filled (electric green); secondary = outline. */
  kind: 'primary' | 'secondary';
  /**
   * Behavior. open_link opens `url`; advance triggers the step's /advance.
   * The voltr_* actions drive MarketPryce Social's in-app steps: apply to the
   * program, order a product to be shipped, read the brand brief, upload a video.
   */
  action: 'open_link' | 'advance' | 'voltr_start' | 'voltr_pick_product' | 'voltr_upload' | 'voltr_brief' | 'voltr_open_link';
  label: string;
  /**
   * open_link only. The URL to open — either a static https link or, when the
   * step's cta was authored `tracked: true`, this user's per-deal tracked URL
   * injected server-side. Absent when the server had nothing to inject; the
   * client then skips that button rather than render a dead one.
   */
  url?: string;
}

export interface DealStep {
  key: string;
  name: string;
  description: string;
  /**
   * Data-driven buttons. When present, the canonical source for this step's
   * actions — the client renders `ctas` and ignores the legacy fields below.
   * Absent on un-migrated deals / older servers, where the client falls back
   * to the legacy link_url/link_label/cta_label rendering.
   */
  ctas?: DealCta[];
  /** Legacy (pre-`ctas`) advance-button text. Kept for old-server fallback. */
  cta_label?: string;
  /** Legacy reference URL surfaced inline as a tappable "{link_label}" link. */
  link_url?: string;
  /** Legacy display text for the inline link (e.g. "Click here"). */
  link_label?: string;
  /**
   * Optional emphasis string rendered as a warning-styled (yellow) box
   * below the description — e.g. a "fund the deposit from a bank account"
   * caveat. Presentation-only; mirrors the backend `callout` step field
   * (`Deals::StepsConfigSchema`). Absent on most steps.
   */
  callout?: string;
  /**
   * PROVIDER-ONLY step: the named event completes it server-side and the
   * user has no button. Renders as Waiting while active. (Deal Steps v2
   * keeps this meaning unchanged so older builds behave the same.)
   */
  complete_on_event?: WebhookEventName;
  /**
   * Deal Steps v2. A TAPPABLE step the provider can ALSO confirm: the user's
   * tap marks it done (blue), the provider's postback turns it green with a
   * timestamp (`action.step_confirmations[key]`). Presence never changes how
   * the active step renders; it is informational for the client.
   */
  confirm_on_event?: WebhookEventName;
  /**
   * Deal Steps v2. Copy shown INSTEAD of `description` while a
   * `complete_on_event` step is the active (Waiting) one. The server also
   * copies it over `description` until the action pays, so builds without
   * this field still read the right thing.
   */
  waiting_description?: string;
  active_when?: { status: ActionStatus[] };
  /**
   * Server-side schema marker that this step is a final outcome
   * (paid/rejected/expired). The mobile client does NOT branch on this
   * field — it derives terminal-failure rendering from
   * `TERMINAL_FAILURE_STATUSES.includes(action.status)` so both layers
   * read from the same source of truth (the action lifecycle). Kept on
   * the type only so we don't crash deserializing it.
   */
  terminal?: boolean;
}

/**
 * Wrapper preserved on purpose — matches the literal server payload
 * (`steps_config: { steps: [...] }`). Future config keys (e.g.
 * `version`, `flags`) live on this object alongside `steps`.
 */
export interface DealStepsConfig {
  steps: DealStep[];
}

/**
 * Server-driven marketing copy for the Refer tab + deal landing surfaces.
 * All fields optional — mobile falls back to inline defaults for any
 * missing field. Mirrors `Deals::MarketingCopySchema` on the backend.
 */
export interface DealMarketingCopy {
  eyebrow?: string;
  hero_subhead?: string;
  hero_body_lines?: string[];
  how_it_works?: { title: string; body: string }[];
  friend_steps?: string[];
  faq?: { q: string; a: string }[];
  compliance_disclosure?: string;
  /**
   * SMS body the nudge flow seeds for THIS deal. Absent for deals with no
   * authored copy — the client falls back to a brandless generic rather than
   * borrowing another deal's product name and dollar amount.
   * Supports the {{first_name}} / {{your_link}} merge tags.
   */
  nudge_template?: string;
  /** MarketPryce Social (Voltr) deal page copy. Every section hides when absent or empty. */
  social?: SocialDealCopy;
}

export interface Deal {
  id: number;
  slug: string;
  brand_name: string;
  /** Offer name that tells sibling deals apart ("SoFi Invest"); brand_name is the company ("SoFi"). */
  display_name: string;
  brand_category: string;
  title: string;
  description: string;
  terms: string | null;
  // EFFECTIVE (possibly bonus-boosted) amounts for THIS user. The server
  // serializes the boosted value into the existing fields so every reader
  // shows correct dollars with zero changes — never re-derive a rate here.
  user_cut_cents: number;
  referrer_cut_cents: number;
  // Bonus-engine additions (backend PR #149, all additive — optional so the
  // app degrades cleanly against an older server / cached payload). The
  // base_* fields carry the UNBOOSTED amounts so the UI can render
  // "~~$20~~ $40" without doing rate math; badges are display strings
  // ("2x") per cut; *_ends_at is the paying bonus window's end (ISO8601,
  // null = no boost or open-ended).
  base_user_cut_cents?: number;
  base_referrer_cut_cents?: number;
  user_bonus_badge?: string | null;
  referrer_bonus_badge?: string | null;
  user_bonus_ends_at?: string | null;
  referrer_bonus_ends_at?: string | null;
  /**
   * What to PRINT for each side when the brand adds its own top-up (SoFi
   * Checking banks the friend $50, only $20 of it ours). Served by the invite
   * program backend; OPTIONAL until it ships, so every card falls back to the
   * effective cut fields above. Never pick a column on the client beyond
   * "display when present, cut otherwise".
   */
  display_user_cents?: number;
  display_referrer_cents?: number;
  // Server-derived (referrer_cut_cents > 0). Gates whether a deal is
  // shareable/referable; non-referral deals still appear in the Deals tab.
  referrals_enabled: boolean;
  // Deal Steps v2, additive: whether the brand can report progress before
  // paying. The deal screen shows its tracker strip only when this is true
  // (or a report already exists); false or absent hides it (Crypto, Upside,
  // Capital One, or an older server).
  provider_reports_steps?: boolean;
  /** MarketPryce Social (Voltr): draw the social deal page. Absent on an older server. */
  social?: boolean;
  cover_image_url: string | null;
  brand_logo_url: string | null;
  steps_config: DealStepsConfig;
  marketing_copy?: DealMarketingCopy;
  // Monthly referral cap (the partner's global per-month funded-conversion
  // inventory). Server-derived — never recompute on the client. A null cap
  // means uncapped: the availability bar is hidden and nothing is gated.
  // When `full` is true the deal is out of spots this month → share + new
  // signups are blocked server-side; the UI reflects it. `cap_resets_at` is
  // an ISO timestamp (start of next month, America/New_York) for "opens
  // again <date>" copy. See backend BUSINESS_RULES.md → Monthly referral cap.
  monthly_referral_cap: number | null;
  spots_used: number | null;
  spots_left: number | null;
  full: boolean;
  cap_resets_at: string | null;
  // Semantic availability the server derives (Deal#availability_status) so the
  // "almost gone" threshold lives in one place, not each client. null when
  // uncapped. The client maps this to the bar's color/pill.
  availability_status: 'plenty' | 'low' | 'full' | null;
}
